import type React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { theme, card, type as text } from "../../theme";
import { StatusTag } from "../Drawing";

// One group of controls on a standard page: a clause, an Annex A theme, a CAF principle, a SOC 2
// criteria series, a CE theme, a law. Same header everywhere: chevron, the standard's colour
// swatch, the title in the section-title scale with an optional one-line hint beneath, the count
// and the progress within the group. Collapsible; the rows (and their detail) are the children.

/** Plural of a count noun: "criterion" → "criteria", "category" → "categories", else "+s". */
export function pluralNoun(noun: string): string {
  if (noun === "criterion") return "criteria";
  if (/[^aeiou]y$/.test(noun)) return `${noun.slice(0, -1)}ies`;
  return `${noun}s`;
}

export function ControlGroup({ title, hint, accent, count, noun = "control", compliant, gap, extra, expanded, onToggle, children }: {
  title: React.ReactNode;
  /** One line under the title: the objective a CAF principle sits in, a clause group's purpose. */
  hint?: React.ReactNode;
  /** The standard's swatch (FW_COLOR); a law's colour on the Legal page. */
  accent: string;
  count: number;
  /** Singular noun for the count: "control", "clause", "outcome", "criterion", "obligation". */
  noun?: string;
  compliant?: number;
  gap?: number;
  /** Anything else that belongs in the header (a "Standard" badge, a percentage). */
  extra?: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}) {
  const plural = count === 1 ? noun : pluralNoun(noun);
  return (
    <section style={{ ...card, padding: 0, overflow: "hidden" }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
          background: theme.color.panelAlt, border: "none",
          cursor: "pointer", textAlign: "left", flexWrap: "wrap",
        }}
      >
        <span aria-hidden="true" style={{ display: "flex", color: theme.color.faint, flexShrink: 0 }}>
          {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </span>
        <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: accent, flexShrink: 0 }} />
        <span style={{ flex: "1 1 240px", minWidth: 0 }}>
          <span style={{ ...text.sectionTitle, margin: 0, display: "block" }}>{title}</span>
          {hint && <span style={{ ...text.hint, display: "block" }}>{hint}</span>}
        </span>
        {extra}
        <span style={{ ...text.meta, flexShrink: 0 }}>{count} {plural}</span>
        {!!compliant && compliant > 0 && <StatusTag tone="met">{compliant} compliant</StatusTag>}
        {!!gap && gap > 0 && <StatusTag tone="gap">{gap} gap{gap !== 1 ? "s" : ""}</StatusTag>}
      </button>
      {expanded && children}
    </section>
  );
}

/**
 * Rows that belong to no group, framed like a group body. The first row's top border is
 * hidden under the frame's own.
 */
export function ControlListFrame({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <div style={{ marginTop: -1 }}>{children}</div>
    </div>
  );
}

/** Stack of groups with the standard spacing. */
export function ControlGroupStack({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>;
}
